import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Image, FileText, Video, CheckCircle, Clock, AlertTriangle,
  Eye, ChevronLeft, ChevronRight, CalendarDays, RefreshCw, Plus
} from 'lucide-react';

const API = '/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  pending_admin_review: { label: 'Pending Admin', color: 'bg-purple-100 text-purple-700' },
  changes_requested: { label: 'Changes Needed', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  scheduled: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600' },
};

const STATUS_DOT = {
  under_review: 'bg-blue-500',
  pending_admin_review: 'bg-purple-500',
  changes_requested: 'bg-amber-500',
  approved: 'bg-emerald-500',
  scheduled: 'bg-indigo-500',
  rejected: 'bg-red-500',
  draft: 'bg-slate-400',
};

const PLATFORM_COLORS = {
  instagram: 'bg-pink-100 text-pink-700',
  facebook: 'bg-blue-100 text-blue-700',
  linkedin: 'bg-sky-100 text-sky-700',
  twitter: 'bg-slate-100 text-slate-700',
  website: 'bg-violet-100 text-violet-700',
};

const TYPE_ICON = { image: Image, video: Video, blog: FileText };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>;
}

export default function ContentCalendarPage() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [calData, setCalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const fetchCalendar = () => {
    setLoading(true);
    axios.get(`${API}/social/calendar`, { params: { year, month }, withCredentials: true })
      .then(r => setCalData(r.data.days || {}))
      .catch(() => setCalData({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCalendar(); }, [year, month]);

  const syncInsights = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/social/insights/sync-all`, {}, { withCredentials: true });
      toast.success('Insights synced');
      fetchCalendar();
    } catch {
      toast.error('Sync failed');
    } finally { setSyncing(false); }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const handleDragStart = (e, contentId, fromDay) => {
    setDragItem({ contentId, fromDay });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragItem || !day) return;
    const targetKey = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    if (targetKey === dragItem.fromDay) { setDragItem(null); return; }
    try {
      await axios.put(`${API}/social/content/${dragItem.contentId}/reschedule`, { scheduled_date: targetKey }, { withCredentials: true });
      toast.success(`Rescheduled to ${monthNames[month-1]} ${day}`);
      fetchCalendar();
    } catch {
      toast.error('Failed to reschedule');
    }
    setDragItem(null);
  };

  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let week = new Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const selectedKey = selectedDay ? `${year}-${String(month).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}` : null;
  const selectedPosts = selectedKey ? (calData[selectedKey] || []) : [];

  // Count totals for the month
  const allPosts = Object.values(calData).flat();
  const scheduledCount = allPosts.filter(p => p.status === 'approved' || p.status === 'scheduled').length;
  const publishedCount = allPosts.filter(p => p.publications && p.publications.length > 0).length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-indigo-500" />
            Content Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Schedule, track, and manage your content posts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={syncInsights} disabled={syncing} data-testid="cal-sync-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button size="sm" onClick={() => navigate('/social/upload')} data-testid="cal-new-post-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Post
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">This Month</p>
              <p className="text-2xl font-bold text-slate-900">{allPosts.length}</p>
            </div>
            <CalendarDays className="h-5 w-5 text-slate-300" />
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">Scheduled</p>
              <p className="text-2xl font-bold text-indigo-600">{scheduledCount}</p>
            </div>
            <Clock className="h-5 w-5 text-indigo-300" />
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">Published</p>
              <p className="text-2xl font-bold text-emerald-600">{publishedCount}</p>
            </div>
            <CheckCircle className="h-5 w-5 text-emerald-300" />
          </CardContent>
        </Card>
      </div>

      {/* Calendar */}
      <Card className="border-slate-200" data-testid="content-calendar">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="cal-prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-base font-semibold min-w-[160px] text-center text-slate-800">
                {monthNames[month-1]} {year}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="cal-next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-64" />
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-px text-center text-xs font-medium text-slate-400 mb-1">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="py-1.5">{d}</div>)}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {weeks.flat().map((day, i) => {
                  if (!day) return <div key={`e-${i}`} className="h-20 bg-slate-50/30 rounded" />;
                  const key = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const posts = calData[key] || [];
                  const isToday = key === todayStr;
                  const isSelected = day === selectedDay;
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                      onDragOver={(e) => { e.preventDefault(); setDropTarget(day); }}
                      onDragLeave={() => setDropTarget(null)}
                      onDrop={(e) => handleDrop(e, day)}
                      className={`h-20 rounded-lg border flex flex-col p-1.5 text-sm transition-all cursor-pointer
                        ${isSelected ? 'bg-indigo-50 border-indigo-400 ring-1 ring-indigo-300' : isToday ? 'bg-blue-50/50 border-blue-200' : 'border-slate-100 hover:border-slate-300 hover:bg-slate-50'}
                        ${dropTarget === day && dragItem ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''}
                      `}
                      data-testid={`cal-day-${day}`}
                    >
                      <span className={`text-xs font-medium leading-none ${isToday ? 'text-blue-600 font-bold' : 'text-slate-600'}`}>{day}</span>
                      {posts.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1">
                          {posts.slice(0, 3).map((p, j) => {
                            const dotColor = p.status === 'approved' ? 'bg-emerald-400' : p.status === 'scheduled' ? 'bg-indigo-400' : p.status === 'under_review' ? 'bg-blue-400' : 'bg-slate-300';
                            return (
                              <span key={j} className={`w-2 h-2 rounded-full ${dotColor}`} title={`${p.title} (${p.status})`} />
                            );
                          })}
                          {posts.length > 3 && <span className="text-[8px] text-slate-400 leading-none ml-0.5">+{posts.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected day detail panel */}
              {selectedDay && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-semibold text-slate-700 mb-3">
                    {monthNames[month-1]} {selectedDay}, {year}
                    <span className="text-xs font-normal text-slate-400 ml-2">
                      {selectedPosts.length} post{selectedPosts.length !== 1 ? 's' : ''}
                    </span>
                  </p>
                  {selectedPosts.length === 0 ? (
                    <div className="text-center py-6 text-slate-400">
                      <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No posts on this day</p>
                      <Button size="sm" variant="link" className="text-xs mt-1" onClick={() => navigate('/social/upload')}>
                        Create a post
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedPosts.map(p => {
                        const m = p.metrics || {};
                        const hasPubs = p.publications && p.publications.length > 0;
                        const hasMetrics = m.impressions > 0 || m.engagement > 0;
                        const TypeIcon = TYPE_ICON[p.content_type] || FileText;
                        const isBlog = p.content_type === 'blog';
                        const imgUrl = p.image_url || (p.file?.url) || (p.file?.storage_path ? `/api/social/media/${p.file.storage_path}` : null);
                        return (
                          <div
                            key={p.content_id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, p.content_id, selectedKey)}
                            className="rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing bg-white"
                            data-testid={`cal-item-${p.content_id}`}
                          >
                            <button
                              onClick={() => navigate(`/social/content/${p.content_id}`)}
                              className="flex items-start gap-3 w-full text-left p-3"
                            >
                              {imgUrl ? (
                                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-slate-100 border border-slate-100">
                                  <img src={imgUrl} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                                </div>
                              ) : (
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${isBlog ? 'bg-violet-50' : p.content_type === 'video' ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                                  <TypeIcon className={`h-5 w-5 ${isBlog ? 'text-violet-500' : p.content_type === 'video' ? 'text-amber-500' : 'text-emerald-500'}`} />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-slate-700 truncate">{p.title}</span>
                                  <StatusBadge status={p.status} />
                                </div>
                                {p.caption && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{p.caption}</p>}
                                {/* Platforms */}
                                {p.platforms && p.platforms.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {p.platforms.map((plat, pi) => (
                                      <span key={pi} className={`text-[9px] px-1.5 py-0.5 rounded ${PLATFORM_COLORS[plat.toLowerCase()] || 'bg-slate-100 text-slate-500'}`}>
                                        {plat}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </button>
                            {/* Published badges */}
                            {hasPubs && (
                              <div className="px-3 pb-2 flex flex-wrap gap-1">
                                {p.publications.map((pub, pi) => (
                                  <Badge key={pi} variant="outline" className="text-[9px] gap-1 bg-emerald-50 border-emerald-200 text-emerald-700">
                                    <CheckCircle className="h-2.5 w-2.5" /> {pub.platform} posted
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {/* Metrics */}
                            {hasMetrics && (
                              <div className="px-3 pb-2 flex gap-3 text-[10px] text-slate-400">
                                {m.impressions > 0 && <span><Eye className="inline h-3 w-3 mr-0.5" />{m.impressions.toLocaleString()}</span>}
                                {m.likes > 0 && <span>&#9825; {m.likes}</span>}
                                {m.comments > 0 && <span>&#128172; {m.comments}</span>}
                                {m.shares > 0 && <span>&#8644; {m.shares}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Legend */}
              <div className="flex gap-4 mt-4 pt-3 border-t flex-wrap">
                {Object.entries(STATUS_DOT).map(([status, dotColor]) => (
                  <div key={status} className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                    <span className="text-[10px] text-slate-400">{STATUS_CONFIG[status]?.label || status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
